import { useRef } from "react";
import { Elements, useStripe } from "@stripe/react-stripe-js";
import type { StripeExpressCheckoutElementClickEvent } from "@stripe/stripe-js";
import { useFormContext } from "react-hook-form";
import { t } from "ttag";

import { HookedCreditCardInput } from "@/app/components/legacy-components";
import { env } from "@/config/env";
import type { EventPrefix } from "@/features/analytics/models";
import { EVENT_PREFIX } from "@/features/analytics/models";
import { trackPaymentMethodSelected } from "@/features/analytics/trackers/payment-method-selected";
import type { ContactInfoFormValues } from "@/features/onboarding/hooks";
import { useCheckout, useOnboardingUser } from "@/features/onboarding/hooks";
import { useOnboardingStore } from "@/features/onboarding/stores";
import { ApplePayButton } from "@/features/payments/components";
import { KlarnaButton } from "@/features/payments/components/klarna-button";
import { calculateDiscountedTotal } from "@/features/products/utils";
import { CreditCardAuthorizationField } from "@/shared/components";
import { PAYMENT_METHOD_TYPE } from "@/shared/domains";
import { CURRENCY } from "@/shared/models";
import { AnimatedFields, Heading, ShieldCheckIcon } from "@/shared/ui";

interface Props {
  onClick?: VoidFunction;
  headingAlign?: "left" | "center";
  eventPrefix?: EventPrefix;
  /**
   * Renders the express-checkout wallets (Apple/Google Pay) before Klarna.
   * Off by default so the existing onboarding order is unchanged.
   */
  walletsFirst?: boolean;
}

export const PaymentFieldset = ({
  onClick,
  headingAlign = "center",
  eventPrefix = EVENT_PREFIX.NONE,
  walletsFirst = false,
}: Props) => {
  const stripe = useStripe();
  const selectedCategories = useOnboardingStore(
    (state) => state.selectedCategories,
  );
  const metadata = useOnboardingStore((state) => state.urlParams);

  const user = useOnboardingUser();

  const checkoutDiscount = useOnboardingStore(
    (state) => state.checkoutDiscount,
  );

  const urlParams = useOnboardingStore((state) => state.urlParams);

  const form = useFormContext<ContactInfoFormValues>();
  const creditCardTracked = useRef(false);

  const {
    klarna: { available: klarnaAvailable },
  } = useCheckout({});

  const handleExpressCheckoutClick = async (
    evt: StripeExpressCheckoutElementClickEvent,
  ) => {
    form.setValue("paymentMethodType", PAYMENT_METHOD_TYPE.APPLE_PAY);

    const isValid = await form.trigger();

    if (isValid) {
      trackPaymentMethodSelected({
        paymentMethod: PAYMENT_METHOD_TYPE.APPLE_PAY,
        userId: user?.email,
        eventPrefix,
      });

      onClick?.();
      evt.resolve();
    }
  };

  const isKlarnaAvailable =
    klarnaAvailable && (Boolean(urlParams.klarna) || env.VITE_KLARNA_ENABLED);

  const selectedPrices = selectedCategories
    .map((cat) => cat.selectedProduct?.selectedPrice)
    .filter((price) => price !== undefined);

  const totalInCents = checkoutDiscount?.isValid
    ? checkoutDiscount.totalAfterDiscount * 100
    : calculateDiscountedTotal(selectedCategories) * 100;

  const price = selectedPrices[0];

  const priceDiscountCode = selectedPrices.find(
    (price) => price.discount?.isValid,
  )?.discount?.code;

  const couponCode = checkoutDiscount?.code ?? priceDiscountCode;

  if (!user) {
    return null;
  }

  const klarnaButton = isKlarnaAvailable ? (
    <KlarnaButton
      onClick={() => {
        form.setValue("paymentMethodType", PAYMENT_METHOD_TYPE.KLARNA);
        trackPaymentMethodSelected({
          paymentMethod: PAYMENT_METHOD_TYPE.KLARNA,
          userId: user.email,
          eventPrefix,
        });
      }}
      type="submit"
      className="w-1/2"
    />
  ) : null;

  const walletButton =
    price && env.VITE_APPLE_PAY_ENABLED ? (
      <Elements
        stripe={stripe}
        options={{
          mode: "subscription",
          amount: Math.round(totalInCents),
          currency: CURRENCY.USD,
          appearance: {
            variables: {
              borderRadius: "8px",
            },
          },
        }}
      >
        <ApplePayButton
          price={price}
          user={user}
          metadata={metadata}
          onClick={handleExpressCheckoutClick}
          className="grow"
          discount={couponCode}
        />
      </Elements>
    ) : null;

  return (
    <fieldset className="flex flex-col gap-3">
      <Heading
        size="small"
        level={3}
        color="primary"
        weight="medium"
        align={headingAlign === "center" ? "center" : undefined}
      >
        {t`Payment method`}
      </Heading>

      <div className="space-y-4">
        <div className="flex gap-2">
          {/* Render wallets before Klarna when requested so DOM/tab order (not
              just the visual order) puts the express-checkout option first. */}
          {walletsFirst ? (
            <>
              {walletButton}
              {klarnaButton}
            </>
          ) : (
            <>
              {klarnaButton}
              {walletButton}
            </>
          )}
        </div>

        <div className="inline-flex gap-1">
          <ShieldCheckIcon className="size-4 text-green-500" />

          <p className="text-sm text-primary-950">{t`Your data is protected by HIPAA. All transactions are secured and encrypted.`}</p>
        </div>

        <AnimatedFields>
          <div className="mf-masked mf-listen flex flex-col gap-2">
            <HookedCreditCardInput
              id="onboarding-cc-number"
              name="creditCard"
              label={t`Card number *`}
              control={form.control}
              error={form.formState.errors.creditCard?.message}
              compact={true}
              onBlur={() => {
                if (creditCardTracked.current) {
                  return;
                }
                creditCardTracked.current = true;
                trackPaymentMethodSelected({
                  paymentMethod: PAYMENT_METHOD_TYPE.CARD,
                  userId: user.email,
                  eventPrefix,
                });
              }}
            />

            <CreditCardAuthorizationField control={form.control} />
          </div>
        </AnimatedFields>
      </div>
    </fieldset>
  );
};
